import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuthContextValueHook } from "@/contexts/AuthContext";
import { useChangeCardMutation, useStartSubscriptionMutation } from "@/hooks/ReactQuery/SubscriptionReactQuery/SubscriptionReactQuery";

/**
 * 토스 카드 등록 창에서 돌아오는 곳.
 * /billing/success?authKey&customerKey&userPageId&mode(start|card)&subscriptionId → 서버에 구독 시작/카드 변경 요청 후 요금제 탭으로
 * /billing/fail?code&message&userPageId → 실패 안내
 */
export default function Component(props) {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const authContextValueHook = useAuthContextValueHook();
    const startMutation = useStartSubscriptionMutation();
    const changeCardMutation = useChangeCardMutation();
    const [error, setError] = useState(null);
    const sentRef = useRef(false);

    const success = location.pathname.endsWith('/success');
    const userPageId = searchParams.get('userPageId');
    const planUrl = `/mypage/${userPageId}?tabType=PLAN`;

    useEffect(() => {
        // 로그인 복원(토큰 재발급)이 끝난 뒤 한 번만 요청
        if (!success || sentRef.current || !authContextValueHook?.isReady) return;
        if (!authContextValueHook?.isLoggedIn) {
            setError('로그인이 필요해요. 다시 로그인한 뒤 시도해 주세요.');
            return;
        }
        sentRef.current = true;

        const body = { userPageId, authKey: searchParams.get('authKey'), customerKey: searchParams.get('customerKey') };
        const options = {
            onSuccess: () => navigate(planUrl, { replace: true }),
            onError: (e) => setError(e?.response?.data?.message || '결제를 완료하지 못했어요.'),
        };
        if (searchParams.get('mode') === 'card') {
            changeCardMutation.mutate({ id: searchParams.get('subscriptionId'), body }, options);
        } else {
            startMutation.mutate({ body }, options);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [success, authContextValueHook?.isReady, authContextValueHook?.isLoggedIn]);

    const failMessage = !success ? (searchParams.get('message') || '카드 등록을 완료하지 못했어요.') : error;

    return (
        <div className="flex min-h-screen items-center justify-center bg-[#f5f5f7] px-4">
            <div className="w-full max-w-[420px] rounded-3xl bg-white p-8 text-center ring-1 ring-black/[0.06]">
                {failMessage ? (
                    <>
                        <p className="text-3xl" aria-hidden="true">⚠️</p>
                        <p className="mt-3 text-lg font-bold text-gray-900">결제를 완료하지 못했어요</p>
                        <p className="mt-1 text-sm text-gray-500 break-keep">{failMessage}</p>
                        <Link to={userPageId ? planUrl : '/'} className="mt-6 inline-flex rounded-xl bg-gray-900 px-5 py-3 text-sm font-bold text-white hover:bg-gray-700">요금제로 돌아가기</Link>
                    </>
                ) : (
                    <>
                        <span className="mx-auto block h-9 w-9 animate-spin rounded-full border-[3px] border-gray-200 border-t-gray-900" aria-hidden="true" />
                        <p className="mt-4 text-lg font-bold text-gray-900">결제를 처리하고 있어요</p>
                        <p className="mt-1 text-sm text-gray-500">창을 닫지 말고 잠시만 기다려 주세요.</p>
                    </>
                )}
            </div>
        </div>
    );
}
